import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  AuthAuditLogEntry,
  AuthEvent,
  CatalogAuditAction,
  CatalogAuditEntry,
  CatalogUndoManyResult,
  Location,
  ShopAuditAction,
  ShopAuditLogEntry,
  UserListEntry,
} from "../api/types";
import {
  ACTION_GROUPS,
  UNDOABLE_ACTIONS,
  actionLabel,
  actionSummary,
  actionTone,
  undoneSaleId,
  presenceBadges,
  presenceDetail,
  sessionFlagged,
  authEventLabel,
  authEventSummary,
  authEventTone,
  CATALOG_UNDOABLE_ACTIONS,
  catalogActionLabel,
  catalogActionTone,
  catalogSummary,
} from "../components/actionLogUi";
import { ReasonModal } from "../components/ui/ReasonModal";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { Select } from "../components/ui/Select";
import { useAuth } from "../state/AuthContext";
import { businessDayKey, businessDaysAgo } from "../utils/businessDay";

const PAGE_SIZE = 50;

type RangePreset = "today" | "7d" | "30d" | "custom";

/** Owner rulings 2026-08-04/05: one page — Floor, Catalog, and (owner-only)
 * Security. Managers get Catalog because they can already edit the catalog;
 * hiding the record of their own edits from them protects nothing. */
type LogTab = "floor" | "catalog" | "security";

const CATALOG_ACTIONS: CatalogAuditAction[] = [
  "ITEM_CREATED",
  "ITEM_UPDATED",
  "ITEM_ARCHIVED",
  "ITEM_RESTORED",
  "DRAFT_FINALIZED",
  "TEMPLATE_CREATED",
  "TEMPLATE_UPDATED",
  "ACTION_UNDONE",
  "PAPER_DELETED",
  "PAPER_LINKED",
  "PAPER_UNLINKED",
  "PAPER_RELEASED",
  "FACTURA_WAIT_DISMISSED",
  "DELIVERY_RECEIVE_UNDONE",
  "DELIVERY_DOCUMENT_ADDED",
  "DELIVERY_DOCUMENT_DELETED",
  "PAPER_DETAILS_TYPED",
  "DELIVERY_MOVED",
  "FACTURA_WAIT_REOPENED",
  // G-170: the money edits.
  "LOT_CORRECTED",
  "CATEGORY_CREATED",
  "CATEGORY_PRICING_CHANGED",
  "RUNNING_COST_CREATED",
  "RUNNING_COST_CHANGED",
  "RUNNING_COST_DELETED",
  "EXPENSE_CREATED",
  "EXPENSE_CHANGED",
  "EXPENSE_DELETED",
  // G-167 (OD-283)
  "SHOPIFY_ORDER_ASSIGNED",
  "SHOPIFY_ORDER_PASSED",
];

/** REFRESH is deliberately absent — it lives behind the noise toggle. */
const SECURITY_EVENTS: AuthEvent[] = [
  "LOGIN_SUCCESS",
  "LOGIN_FAILURE",
  "LOCKED",
  "LOGOUT",
  "PIN_SET",
  "PIN_VIEWED",
  "FOB_ISSUED",
  "FOB_REVOKED",
  "PASSWORD_RESET",
  "INVITE_ISSUED",
  "INVITE_CANCELLED",
  "ACCOUNT_SETUP_COMPLETED",
  "EMAIL_VERIFIED",
  "EMAIL_CHANGE_REQUESTED",
  "EMAIL_CHANGED",
  "AI_INSTRUCTIONS_PUBLISHED",
  "AI_INSTRUCTIONS_ROLLED_BACK",
  "PAYROLL_PAYMENT_RECORDED",
  "PAYROLL_PAYMENT_DELETED",
  "PAYROLL_ADJUSTMENT_RECORDED",
  "PAYROLL_ADJUSTMENT_DELETED",
  "PAY_TERMS_CHANGED",
  "COMMISSION_RULE_CHANGED",
  "COURIER_PAYOUT_RULE_CREATED",
  "COURIER_PAYOUT_RULE_CHANGED",
];

/** Time only — the range picker already says which days are on screen. */
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/**
 * G-17 Action log. Every shop-floor action has been recorded in-transaction
 * since the kiosk shipped; until now nothing could read it. Owners pick a
 * shop, managers are pinned to their own by the server (the picker is simply
 * absent for them — the API refuses a foreign id regardless).
 */
export function ActionLogPage() {
  const { user, hasRole } = useAuth();
  const ownerScoped = hasRole("OWNER");

  /*
   * G-105 — the Team page's "See all" link lands here already filtered to one
   * person. SEEDED, not bound: these become ordinary state on the first render
   * and the URL is never read again, because the filters below are driven by
   * the controls and the `requestSeq` guard assumes exactly that. Changing a
   * filter here does not rewrite the URL, and that is deliberate.
   */
  const [searchParams] = useSearchParams();
  // Only ever called from a `useState` initialiser, which React runs once.
  const seed = <T,>(key: string, fallback: T, ok: (v: string) => T | null): T => {
    const raw = searchParams.get(key);
    if (raw === null) return fallback;
    return ok(raw) ?? fallback;
  };

  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState(() =>
    seed("locationId", "", (v) => v),
  );
  const [people, setPeople] = useState<UserListEntry[]>([]);

  const [preset, setPreset] = useState<RangePreset>(() =>
    seed<RangePreset>("preset", "today", (v) =>
      v === "today" || v === "7d" || v === "30d" || v === "custom" ? v : null,
    ),
  );
  const [customFrom, setCustomFrom] = useState(businessDayKey(new Date()));
  const [customTo, setCustomTo] = useState(businessDayKey(new Date()));
  const [actorUserId, setActorUserId] = useState(() =>
    seed("actorUserId", "", (v) => v),
  );
  const [action, setAction] = useState<"" | ShopAuditAction>("");

  const [rows, setRows] = useState<ShopAuditLogEntry[]>([]);
  // Security tab (owner/admin only) — a separate stream because account events
  // have no location to scope a manager by. See auth-audit-log.service.ts.
  // G-105: the Team page links straight to the tab a row came from. Security
  // is honoured only for an owner — a manager asking for it lands on Floor,
  // the same tab they would get by default (OD-25).
  const [tab, setTab] = useState<LogTab>(() =>
    seed<LogTab>("tab", "floor", (v) =>
      v === "floor" || v === "catalog" || (v === "security" && ownerScoped)
        ? v
        : null,
    ),
  );
  const [secRows, setSecRows] = useState<AuthAuditLogEntry[]>([]);
  const [secEvent, setSecEvent] = useState<"" | AuthEvent>("");
  const [showTokenActivity, setShowTokenActivity] = useState(false);
  // Catalog tab (G-54, owner+manager): items and templates, no location.
  const [catRows, setCatRows] = useState<CatalogAuditEntry[]>([]);
  const [catAction, setCatAction] = useState<"" | CatalogAuditAction>("");
  const [expanded, setExpanded] = useState<string | null>(null);
  // G-51: the row being undone — a required reason via the kit modal.
  const [undoing, setUndoing] = useState<ShopAuditLogEntry | null>(null);
  const [undoBusy, setUndoBusy] = useState(false);
  // G-115: catalog rows — one at a time, or the whole staging pass at once
  // (a review pass promotes dozens of items; one by one is not an option).
  const [catUndoing, setCatUndoing] = useState<CatalogAuditEntry | null>(null);
  const [catSelected, setCatSelected] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [catBulkOpen, setCatBulkOpen] = useState(false);
  const [catReport, setCatReport] = useState<CatalogUndoManyResult | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ownerScoped) return;
    void api
      .listLocations()
      .then((list) => {
        const active = list.filter((loc) => loc.isActive);
        setLocations(active);
        setLocationId((cur) => cur || active[0]?.id || "");
      })
      .catch(() => setLocations([]));
  }, [ownerScoped]);

  useEffect(() => {
    void api.listUsers().then(setPeople).catch(() => setPeople([]));
  }, []);

  const range = useMemo(() => {
    // KI-120: Barcelona days, as the server counts them (OD-17) — the UTC date
    // showed the day before between midnight and 01:00/02:00.
    const today = businessDayKey(new Date());
    if (preset === "today") return { from: today, to: today };
    if (preset === "7d") return { from: businessDaysAgo(6), to: today };
    if (preset === "30d") return { from: businessDaysAgo(29), to: today };
    return { from: customFrom, to: customTo };
  }, [preset, customFrom, customTo]);

  const query = useMemo(
    () => ({
      ...(ownerScoped ? { locationId } : {}),
      ...(actorUserId ? { actorUserId } : {}),
      ...(action ? { action } : {}),
      from: range.from,
      to: range.to,
      take: PAGE_SIZE,
    }),
    [ownerScoped, locationId, actorUserId, action, range.from, range.to],
  );

  /** id → name, so a security row reads "viewed by Ana" not a raw UUID. */
  const nameById = useMemo(
    () => new Map(people.map((p) => [p.id, p.fullName])),
    [people],
  );

  const ready = !ownerScoped || Boolean(locationId);

  /**
   * Every filter change starts a request, and responses can land out of
   * order — changing Today → Last 7 days left the 7-day rows on screen for a
   * moment and then replaced them with the older Today response, which had
   * simply been slower. Each request takes a ticket; only the newest one is
   * allowed to write state. Found in the browser, not by reading the code.
   */
  const requestSeq = useRef(0);

  const refresh = useCallback(async () => {
    if (!ready) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const list = await api.listShopAuditLog(query);
      if (seq !== requestSeq.current) return;
      setRows(list);
      setHasMore(list.length === PAGE_SIZE);
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      setError(
        err instanceof Error ? err.message : "Could not load the action log.",
      );
      setRows([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [query, ready]);

  useEffect(() => {
    if (tab === "floor") void refresh();
  }, [refresh, tab]);

  // Security stream. Shares `requestSeq` with the floor fetch so switching
  // tabs mid-flight cannot let a late response paint over the new tab.
  const refreshSecurity = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const list = await api.listAuthAuditLog({
        ...(actorUserId ? { userId: actorUserId } : {}),
        ...(secEvent ? { event: secEvent } : {}),
        from: range.from,
        to: range.to,
        includeTokenActivity: showTokenActivity,
        take: PAGE_SIZE,
      });
      if (seq !== requestSeq.current) return;
      setSecRows(list);
      setHasMore(list.length === PAGE_SIZE);
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      setError(
        err instanceof Error ? err.message : "Could not load the security log.",
      );
      setSecRows([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [actorUserId, secEvent, range.from, range.to, showTokenActivity]);

  useEffect(() => {
    if (tab === "security") void refreshSecurity();
  }, [refreshSecurity, tab]);

  // Catalog stream (G-54). Same shared ticket as the other two tabs.
  const refreshCatalog = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const list = await api.listCatalogAuditLog({
        ...(actorUserId ? { actorUserId } : {}),
        ...(catAction ? { action: catAction } : {}),
        from: range.from,
        to: range.to,
        take: PAGE_SIZE,
      });
      if (seq !== requestSeq.current) return;
      setCatRows(list);
      setCatSelected(new Set());
      setHasMore(list.length === PAGE_SIZE);
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      setError(
        err instanceof Error ? err.message : "Could not load the catalog log.",
      );
      setCatRows([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [actorUserId, catAction, range.from, range.to]);

  useEffect(() => {
    if (tab === "catalog") void refreshCatalog();
  }, [refreshCatalog, tab]);

  const loadMore = async () => {
    // Same ticket: if the filters change mid-page, the extra rows belong to a
    // query nobody is looking at any more and must not be appended.
    const seq = requestSeq.current;
    setLoadingMore(true);
    try {
      if (tab === "catalog") {
        const more = await api.listCatalogAuditLog({
          ...(actorUserId ? { actorUserId } : {}),
          ...(catAction ? { action: catAction } : {}),
          from: range.from,
          to: range.to,
          take: PAGE_SIZE,
          skip: catRows.length,
        });
        if (seq !== requestSeq.current) return;
        setCatRows((cur) => [...cur, ...more]);
        setHasMore(more.length === PAGE_SIZE);
        return;
      }
      if (tab === "security") {
        const more = await api.listAuthAuditLog({
          ...(actorUserId ? { userId: actorUserId } : {}),
          ...(secEvent ? { event: secEvent } : {}),
          from: range.from,
          to: range.to,
          includeTokenActivity: showTokenActivity,
          take: PAGE_SIZE,
          skip: secRows.length,
        });
        if (seq !== requestSeq.current) return;
        setSecRows((cur) => [...cur, ...more]);
        setHasMore(more.length === PAGE_SIZE);
        return;
      }
      const list = await api.listShopAuditLog({ ...query, skip: rows.length });
      if (seq !== requestSeq.current) return;
      setRows((cur) => [...cur, ...list]);
      setHasMore(list.length === PAGE_SIZE);
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err.message : "Could not load more.");
    } finally {
      if (seq === requestSeq.current) setLoadingMore(false);
    }
  };

  if (!user) return null;

  const loadMoreButton = hasMore ? (
    <button
      type="button"
      className="button plain"
      onClick={() => void loadMore()}
      disabled={loadingMore}
    >
      {loadingMore ? "Loading…" : "Load more"}
    </button>
  ) : null;

  // G-115: the rows on this page a person may undo (never an undone one).
  const catSelectable = catRows.filter(
    (row) => CATALOG_UNDOABLE_ACTIONS.has(row.action) && !row.undone,
  );

  return (
    <div className="action-log">
      <header className="page-header">
        <div>
          <h1>Action log</h1>
          <p className="muted">
            {tab === "floor"
              ? "What happened on the shop floor, newest first."
              : tab === "catalog"
                ? "Who changed what in the catalog, newest first."
                : "Sign-ins, credential changes and AI instruction changes, newest first."}
          </p>
        </div>
        {/* Catalog is owner+manager (G-54 ruling 2026-08-05 — matches their
            write access). Security stays OWNER/ADMIN only (OD-25): account
            events span every person with no location to scope a manager by,
            so a manager never sees that segment exist. */}
        <SegmentedControl<LogTab>
          ariaLabel="Which log"
          value={tab}
          onChange={setTab}
          options={[
            { value: "floor", label: "Floor actions" },
            { value: "catalog", label: "Catalog" },
            ...(ownerScoped
              ? [{ value: "security" as const, label: "Security" }]
              : []),
          ]}
        />
      </header>

      <div className="card action-log-filters">
        {ownerScoped && tab === "floor" ? (
          <div className="field">
            <label htmlFor="action-log-shop">Shop</label>
            <Select
              id="action-log-shop"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="action-log-range">Period</label>
          <Select
            id="action-log-range"
            value={preset}
            onChange={(e) => setPreset(e.target.value as RangePreset)}
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Custom range</option>
          </Select>
        </div>

        {preset === "custom" ? (
          <>
            <div className="field">
              <label htmlFor="action-log-from">From</label>
              <input
                id="action-log-from"
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="action-log-to">To</label>
              <input
                id="action-log-to"
                type="date"
                value={customTo}
                min={customFrom}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </div>
          </>
        ) : null}

        <div className="field">
          <label htmlFor="action-log-person">Person</label>
          <Select
            id="action-log-person"
            value={actorUserId}
            onChange={(e) => setActorUserId(e.target.value)}
          >
            <option value="">Everyone</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
                {p.isActive ? "" : " (inactive)"}
              </option>
            ))}
          </Select>
        </div>

        {tab === "catalog" ? (
          <div className="field">
            <label htmlFor="action-log-cat-action">Action</label>
            <Select
              id="action-log-cat-action"
              value={catAction}
              onChange={(e) =>
                setCatAction(e.target.value as "" | CatalogAuditAction)
              }
            >
              <option value="">All actions</option>
              {CATALOG_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {catalogActionLabel(a)}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
        {tab === "floor" ? (
          <div className="field">
            <label htmlFor="action-log-action">Action</label>
            <Select
              id="action-log-action"
              value={action}
              onChange={(e) => setAction(e.target.value as "" | ShopAuditAction)}
            >
              <option value="">All actions</option>
              {ACTION_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.actions.map((a) => (
                    <option key={a} value={a}>
                      {actionLabel(a)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </div>
        ) : null}
        {tab === "security" ? (
          <>
            <div className="field">
              <label htmlFor="action-log-event">Event</label>
              <Select
                id="action-log-event"
                value={secEvent}
                onChange={(e) => setSecEvent(e.target.value as "" | AuthEvent)}
              >
                <option value="">All events</option>
                {SECURITY_EVENTS.map((e) => (
                  <option key={e} value={e}>
                    {authEventLabel(e)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="field">
              {/* Token refreshes fire every 15 minutes per signed-in person and
                  would bury the events a human needs to see. Off by default. */}
              <label className="hint">
                <input
                  type="checkbox"
                  checked={showTokenActivity}
                  onChange={(e) => setShowTokenActivity(e.target.checked)}
                />
                Show token refreshes
              </label>
            </div>
          </>
        ) : null}
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : tab === "catalog" ? (
        catRows.length === 0 ? (
          <p className="muted">
            No catalog changes were recorded in this period. The log records
            from the day it shipped — older items were created before it
            existed.
          </p>
        ) : (
          <>
            {/* G-115: pick the rows of a staging pass and undo them together. */}
            {catSelectable.length > 0 ? (
              <div className="action-log-bulk">
                <button
                  type="button"
                  className="button plain"
                  onClick={() =>
                    setCatSelected(
                      catSelected.size === catSelectable.length
                        ? new Set()
                        : new Set(catSelectable.map((r) => r.id)),
                    )
                  }
                >
                  {catSelected.size === catSelectable.length
                    ? "Clear selection"
                    : `Select all ${catSelectable.length} undoable on this page`}
                </button>
                <button
                  type="button"
                  className="button danger"
                  disabled={catSelected.size === 0}
                  onClick={() => setCatBulkOpen(true)}
                >
                  Undo selected ({catSelected.size})
                </button>
              </div>
            ) : null}
            {catReport ? (
              <div
                className={`alert ${catReport.failed > 0 ? "error" : "success"} action-log-undo-report`}
                role="status"
              >
                <p>
                  {catReport.undone} change{catReport.undone === 1 ? "" : "s"}{" "}
                  undone
                  {catReport.failed > 0
                    ? ` · ${catReport.failed} could not be undone:`
                    : "."}
                </p>
                {catReport.failed > 0 ? (
                  <ul>
                    {catReport.results
                      .filter((r) => !r.ok)
                      .map((r) => (
                        <li key={r.auditLogId}>
                          {r.entityName ? <strong>{r.entityName}: </strong> : null}
                          {r.error}
                        </li>
                      ))}
                  </ul>
                ) : null}
                <button
                  type="button"
                  className="button plain"
                  onClick={() => setCatReport(null)}
                >
                  Dismiss
                </button>
              </div>
            ) : null}
            <ul className="action-log-list">
              {catRows.map((row) => {
                const summary = catalogSummary(row);
                const tone = catalogActionTone(row.action);
                const undoable =
                  CATALOG_UNDOABLE_ACTIONS.has(row.action) && !row.undone;
                return (
                  <li
                    key={row.id}
                    className={`action-log-row${row.undone ? " undone" : ""}`}
                  >
                    <div className="action-log-row-head">
                      {undoable ? (
                        <label className="action-log-select">
                          <input
                            type="checkbox"
                            checked={catSelected.has(row.id)}
                            onChange={(e) =>
                              setCatSelected((cur) => {
                                const next = new Set(cur);
                                if (e.target.checked) next.add(row.id);
                                else next.delete(row.id);
                                return next;
                              })
                            }
                            aria-label={`Select "${row.entityName}" to undo`}
                          />
                        </label>
                      ) : null}
                      <button
                        type="button"
                        className="action-log-row-main"
                        onClick={() =>
                          setExpanded((cur) => (cur === row.id ? null : row.id))
                        }
                        aria-expanded={expanded === row.id}
                      >
                        <span className="action-log-time">
                          <strong>{fmtTime(row.createdAt)}</strong>
                          <span className="muted">{fmtDay(row.createdAt)}</span>
                        </span>
                        <span className="action-log-body">
                          <span className={`action-log-action tone-${tone}`}>
                            {catalogActionLabel(row.action, row.source)}
                            {row.undone ? (
                              <span className="action-log-badge tone-warning">
                                undone by {row.undone.by}
                              </span>
                            ) : null}
                          </span>
                          <span className="action-log-who">
                            {row.entityName}
                            <span className="muted">
                              {" "}
                              · by {row.actor?.fullName ?? "Removed user"}
                            </span>
                          </span>
                          {summary ? (
                            <span className="muted action-log-summary">
                              {summary}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </div>
                    {expanded === row.id ? (
                      <div className="action-log-detail">
                        <dl>
                          <dt>Recorded</dt>
                          <dd>{new Date(row.createdAt).toLocaleString()}</dd>
                          <dt>Entity</dt>
                          <dd>
                            {row.entityType} · {row.entityId}
                          </dd>
                          {row.undone ? (
                            <>
                              <dt>Undone</dt>
                              <dd>
                                by {row.undone.by} ·{" "}
                                {new Date(row.undone.at).toLocaleString()} ·{" "}
                                {row.undone.reason}
                              </dd>
                            </>
                          ) : null}
                        </dl>
                        {row.metadata != null ? (
                          <pre className="action-log-meta">
                            {JSON.stringify(row.metadata, null, 2)}
                          </pre>
                        ) : (
                          <p className="muted">No extra detail was recorded.</p>
                        )}
                        {/* G-115: reverse the damage, never the record. */}
                        {undoable ? (
                          <button
                            type="button"
                            className="button plain action-log-undo"
                            onClick={() => setCatUndoing(row)}
                          >
                            Undo this change
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {loadMoreButton}
          </>
        )
      ) : tab === "security" ? (
        secRows.length === 0 ? (
          <p className="muted">
            No sign-ins or credential changes were recorded in this period.
          </p>
        ) : (
          <>
            <ul className="action-log-list">
              {secRows.map((row) => {
                const summary = authEventSummary(row, nameById);
                const tone = authEventTone(row.event);
                // No user means a failed sign-in against an address that
                // matches nobody — the attempted email IS the row's subject.
                const who = row.user?.fullName ?? row.email ?? "Unknown";
                return (
                  <li key={row.id} className="action-log-row">
                    <button
                      type="button"
                      className="action-log-row-main"
                      onClick={() =>
                        setExpanded((cur) => (cur === row.id ? null : row.id))
                      }
                      aria-expanded={expanded === row.id}
                    >
                      <span className="action-log-time">
                        <strong>{fmtTime(row.createdAt)}</strong>
                        <span className="muted">{fmtDay(row.createdAt)}</span>
                      </span>
                      <span className="action-log-body">
                        <span className={`action-log-action tone-${tone}`}>
                          {authEventLabel(row.event)}
                        </span>
                        <span className="action-log-who">{who}</span>
                        {summary ? (
                          <span className="muted action-log-summary">
                            {summary}
                          </span>
                        ) : null}
                      </span>
                    </button>
                    {expanded === row.id ? (
                      <div className="action-log-detail">
                        <dl>
                          <dt>Recorded</dt>
                          <dd>{new Date(row.createdAt).toLocaleString()}</dd>
                          {row.ip ? (
                            <>
                              <dt>IP address</dt>
                              <dd>{row.ip}</dd>
                            </>
                          ) : null}
                          {row.userAgent ? (
                            <>
                              <dt>Device</dt>
                              <dd>{row.userAgent}</dd>
                            </>
                          ) : null}
                        </dl>
                        {row.metadata != null ? (
                          <pre className="action-log-meta">
                            {JSON.stringify(row.metadata, null, 2)}
                          </pre>
                        ) : (
                          <p className="muted">No extra detail was recorded.</p>
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {loadMoreButton}
          </>
        )
      ) : rows.length === 0 ? (
        <p className="muted">
          Nothing was recorded on the floor in this period.
        </p>
      ) : (
        <>
          <ul className="action-log-list">
            {rows.map((row) => {
              const summary = actionSummary(row);
              const tone = actionTone(row.action);
              const badges = presenceBadges(row);
              const flaggedSession = sessionFlagged(row);
              const locationLine = presenceDetail(row);
              const showArmedBy =
                row.actor == null || row.armedBy.id !== row.actor.id;
              return (
                <li
                  key={row.id}
                  className={`action-log-row${
                    flaggedSession ? " session-flagged" : ""
                  }${row.unscheduled ? " unscheduled" : ""}${
                    row.undone ? " undone" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="action-log-row-main"
                    onClick={() =>
                      setExpanded((cur) => (cur === row.id ? null : row.id))
                    }
                    aria-expanded={expanded === row.id}
                  >
                    <span className="action-log-time">
                      <strong>{fmtTime(row.createdAt)}</strong>
                      <span className="muted">{fmtDay(row.createdAt)}</span>
                    </span>
                    <span className="action-log-body">
                      <span className={`action-log-action tone-${tone}`}>
                        {actionLabel(row.action)}
                        {row.undone ? (
                          <span className="action-log-badge tone-warning">
                            undone by {row.undone.by}
                          </span>
                        ) : null}
                        {badges.map((badge) => (
                          <span
                            key={badge.label}
                            className={`action-log-badge tone-${badge.tone}`}
                          >
                            {badge.label}
                          </span>
                        ))}
                      </span>
                      <span className="action-log-who">
                        {row.actor?.fullName ?? "Removed user"}
                        {showArmedBy ? (
                          <span className="muted">
                            {" "}
                            · tablet armed by {row.armedBy.fullName}
                          </span>
                        ) : null}
                      </span>
                      {summary ? (
                        <span className="muted action-log-summary">{summary}</span>
                      ) : null}
                    </span>
                  </button>
                  {expanded === row.id ? (
                    <div className="action-log-detail">
                      <dl>
                        <dt>Recorded</dt>
                        <dd>{new Date(row.createdAt).toLocaleString()}</dd>
                        {row.entityType ? (
                          <>
                            <dt>Entity</dt>
                            <dd>
                              {row.entityType}
                              {row.entityId ? ` · ${row.entityId}` : ""}
                            </dd>
                          </>
                        ) : null}
                        {locationLine ? (
                          <>
                            <dt>Location</dt>
                            <dd>{locationLine}</dd>
                          </>
                        ) : null}
                        {row.undone ? (
                          <>
                            <dt>Undone</dt>
                            <dd>
                              by {row.undone.by} ·{" "}
                              {new Date(row.undone.at).toLocaleString()} ·{" "}
                              {row.undone.reason}
                            </dd>
                          </>
                        ) : null}
                        {flaggedSession ? (
                          <>
                            <dt>Session</dt>
                            <dd>
                              {row.sessionGeoStatus === "FAR"
                                ? "The tablet was armed far from the shop — every action in this session is under that flag."
                                : "The tablet was armed without a usable location — every action in this session is under that flag."}
                            </dd>
                          </>
                        ) : null}
                      </dl>
                      {row.metadata != null ? (
                        <pre className="action-log-meta">
                          {JSON.stringify(row.metadata, null, 2)}
                        </pre>
                      ) : (
                        <p className="muted">No extra detail was recorded.</p>
                      )}
                      {/* G-151: the undone sale is kept — open it. */}
                      {undoneSaleId(row) ? (
                        <Link
                          className="button plain action-log-undo"
                          to={`/app/sales?sale=${encodeURIComponent(undoneSaleId(row) ?? "")}`}
                        >
                          Open in Sales records
                        </Link>
                      ) : null}
                      {/* G-51: reverse the damage, never the record. */}
                      {UNDOABLE_ACTIONS.has(row.action) && !row.undone ? (
                        <button
                          type="button"
                          className="button plain action-log-undo"
                          onClick={() => setUndoing(row)}
                        >
                          Undo this action
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>

          {loadMoreButton}
        </>
      )}

      {undoing ? (
        <ReasonModal
          title={`Undo "${actionLabel(undoing.action)}"?`}
          message="The stock and money effects are reversed; the log entry itself stays, struck through, with your name and reason beside it."
          reasonMode="required"
          reasonPlaceholder="e.g. double-tap — the sale never happened"
          confirmLabel="Undo it"
          destructive
          busy={undoBusy}
          onConfirm={async (reason) => {
            if (!reason) return;
            setUndoBusy(true);
            try {
              await api.undoAuditAction(undoing.id, reason);
              setUndoing(null);
              await refresh();
            } catch (err: unknown) {
              setError(
                err instanceof Error ? err.message : "Could not undo this action.",
              );
              setUndoing(null);
            } finally {
              setUndoBusy(false);
            }
          }}
          onClose={() => setUndoing(null)}
        />
      ) : null}

      {catUndoing ? (
        <ReasonModal
          title={`Undo "${catalogActionLabel(catUndoing.action, catUndoing.source)}" for ${catUndoing.entityName}?`}
          message={catalogUndoMessage(catUndoing.action)}
          reasonMode="required"
          reasonPlaceholder="e.g. staged twice — this is the duplicate"
          confirmLabel="Undo it"
          destructive
          busy={undoBusy}
          onConfirm={async (reason) => {
            if (!reason) return;
            setUndoBusy(true);
            try {
              await api.undoCatalogAction(catUndoing.id, reason);
              setCatUndoing(null);
              await refreshCatalog();
            } catch (err: unknown) {
              setError(
                err instanceof Error ? err.message : "Could not undo this change.",
              );
              setCatUndoing(null);
              // A refusal usually means the page is stale (undone in another
              // tab, state changed) — show the row as it is now.
              await refreshCatalog();
            } finally {
              setUndoBusy(false);
            }
          }}
          onClose={() => setCatUndoing(null)}
        />
      ) : null}

      {catBulkOpen ? (
        <ReasonModal
          title={`Undo ${catSelected.size} selected change${catSelected.size === 1 ? "" : "s"}?`}
          message="Each created item is deleted and its staging draft returns to Pending; an archive or restore is flipped back. A row the server refuses (stock received, sold, on a delivery, in a recipe) is reported and the rest go through. The log entries stay, struck through, with your name and reason."
          reasonMode="required"
          reasonPlaceholder="e.g. the staging pass ran twice"
          confirmLabel="Undo them"
          destructive
          busy={undoBusy}
          onConfirm={async (reason) => {
            if (!reason) return;
            setUndoBusy(true);
            try {
              const report = await api.undoCatalogActions(
                [...catSelected],
                reason,
              );
              setCatReport(report);
              setCatBulkOpen(false);
              await refreshCatalog();
            } catch (err: unknown) {
              setError(
                err instanceof Error
                  ? err.message
                  : "Could not undo the selected changes.",
              );
              setCatBulkOpen(false);
              await refreshCatalog();
            } finally {
              setUndoBusy(false);
            }
          }}
          onClose={() => setCatBulkOpen(false)}
        />
      ) : null}
    </div>
  );
}

/** G-115: what the single-row undo will do, in the modal's own words. */
function catalogUndoMessage(action: CatalogAuditAction): string {
  switch (action) {
    case "ITEM_CREATED":
    case "DRAFT_FINALIZED":
      return "The item is deleted and its staging draft (if any) returns to Pending. Refused if stock was received against it, it was sold, it is on a delivery or in a recipe — archive it from the item page then. The log entry stays, struck through, with your name and reason.";
    case "ITEM_ARCHIVED":
      return "The item is restored. The log entry stays, struck through, with your name and reason.";
    case "ITEM_RESTORED":
      return "The item is archived again. The log entry stays, struck through, with your name and reason.";
    default:
      return "The log entry stays, struck through, with your name and reason.";
  }
}
